import type { ITenantUsageData } from '@experiences/interfaces';
import { roundTo2Decimals } from '@experiences/util';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import useSWR from 'swr';

import {
    getTenantUsageData,
    licenseUsageUrl,
} from '../../../services/licensing/LicenseUsageService';
import { UiGrid } from '../../common/UiGrid';
import { UiChartLoading } from '../helperComponents/UiConsumableCard/UiChartLoading';

interface ITenantUsage {
    tenantId: string;
    tenantName: string;
    allocated: number;
    consumed: number;
}

export const TenantUsageGrid: React.FC<{
    consumableCode: string;
    tenantId?: string;
    startDate: number | undefined;
    endDate: number | undefined;
}> = ({
    consumableCode, tenantId, startDate, endDate,
}) => {
    const { formatMessage: translate } = useIntl();

    const { data: tenantUsageData } = useSWR(
        {
            url: `${licenseUsageUrl}/tenants`,
            startDate,
            endDate,
            consumableCode,
            tenantId,
        },
        getTenantUsageData,
    );

    const transformedUsageData: ITenantUsage[] | undefined = useMemo(() => {
        if (!tenantUsageData) {
            return undefined;
        }

        const tenantUsageList: ITenantUsage[] = tenantUsageData.consumptionData.map((tenantData: ITenantUsageData) => ({
            tenantId: tenantData.tenantId,
            tenantName: tenantData.tenantName ?? tenantData.tenantId,
            allocated: tenantData.allocated,
            consumed: roundTo2Decimals(tenantData.consumed),
        }));

        if (tenantId) {
            return tenantUsageList.filter(tenant => tenant.tenantId === tenantId);
        }

        return tenantUsageList;
    }, [ tenantId, tenantUsageData ]);

    if (!tenantUsageData) {
        return <UiChartLoading
            width={150}
            height={36} />;
    }

    return (
        <>
            <div id="ui-grid-section">
                <UiGrid<ITenantUsage>
                    dataCy="tenant-usage-ui-grid"
                    tableHeight={`${60 * (transformedUsageData?.length ?? 1) + 100}px`}
                    initialSort={[ { id: 'tenantName' }, { id: 'consumed' } ]}
                    columns={[
                        {
                            accessor: 'tenantName',
                            Header: translate({ id: 'CLIENT_CONSUMABLE_PLATFORM_UNITS_USAGE_TENANT_NAME' }),
                            disableSortBy: false,
                            width: 15,
                        },
                        {
                            accessor: 'allocated',
                            Header: translate({ id: 'CLIENT_CONSUMABLE_PLATFORM_UNITS_USAGE_TENANT_ALLOCATED' }),
                            disableSortBy: false,
                            width: 15,
                        },
                        {
                            accessor: 'consumed',
                            Header: translate({ id: 'CLIENT_CONSUMABLE_PLATFORM_UNITS_USAGE_TENANT_CONSUMED' }),
                            width: 15,
                        },
                    ]}
                    data={transformedUsageData ?? []}
                />
            </div>
        </>
    );
};
